import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectorRef } from '@angular/core';
import { DirectGeometryObject, Polygon, GeometryObject, Feature, FeatureCollection, Point  as GeoJsonPoint } from 'geojson';
import * as L from 'leaflet';
import {
  Control,
  DomEvent,
  DomUtil,
  FeatureGroup,
  geoJSON,
  GeoJSON,
  LatLng,
  LatLngExpression,
  Layer,
  Map,
  Point,
  Projection,
  TileLayer,
  Icon,
  DivIcon
} from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-draw';
import { Subscription } from 'rxjs';
import {MarkerOptions, CircleCanvasIcon, SquareCanvasIcon, StarCanvasIcon, TriangleCanvasIcon, Utils} from '../../classes';
import {IBaseLayer, ILayer, IMapLayer} from '../../interfaces';
import { SubscribeService } from '../../services';

@Component({
  selector: 'geo-map',
  templateUrl: 'map.component.html',
  styleUrls: ['./map.component.less']
})
export class MapComponent implements AfterViewInit, OnDestroy {
  @Input() layer: IMapLayer;
  @Input() outputFormat: 'geojson' | 'wkt' = 'geojson';
  @Input() drawTool = false;
  @Input() geometry: string;
  @Input() inModal = false;
  @Input() defaultControls = true;

  @Output() drawEnd = new EventEmitter();
  @Output() onClear = new EventEmitter();

  defaultCenter: LatLngExpression = [66.416667, 94.25]; // центр РФ
  defaultZoom = 3;

  map: Map;
  drawnItems: FeatureGroup;

  private baseLayers: TileLayer[] = [];
  private sub: Subscription;
  private leafletLayers: any = {};

  constructor(public subscribeService: SubscribeService, private changeDetector: ChangeDetectorRef) {
  }

  ngAfterViewInit() {
    this.createMap();

    this.sub = this.subscribeService.subscribe('map-reload', this, (data: any) => {
      this.refresh();
    });

    if (this.inModal) {
      this.sub.add(
        this.subscribeService.subscribe('modal-shown', this, (data: any) => {
          this.map.invalidateSize(true);
          this.setup();
        })
      );
    }

    this.setup();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  get currentZoom(): number {
    return this.map ? this.map.getZoom() : this.defaultZoom;
  }

  refresh() {
    this.map.eachLayer((item: any) => {
      let newUrl: string;
      if (!item.url) {
        item.url = item._url;
      }
      if (!(item.url && item.redraw && this.baseLayers.indexOf(item) < 0)) {
        return;
      }
      const timestamp: number = Date.now();
      if (item.url.indexOf('?debug=true')) {
        newUrl = `${item.url}&random=${timestamp}`;
      } else {
        newUrl = `${item.url}?random=${timestamp}`;
      }
      item.setUrl(newUrl);
      item.redraw();
    });
  }

  refreshSize() {
    this.map.invalidateSize({animate: true});
  }

  clearMap() {
    if (this.drawnItems) {
      this.drawnItems.clearLayers();
    }
  }

  showGeometry(geom: string, goToFeature = false) {
    const geoJSONObj = this.parseWKT(geom);
    const layer: Layer = geoJSON(geoJSONObj as any, {style: (feature: any) => ({color: '#627cbb'})});
    this.drawnItems.addLayer(layer);
    if (goToFeature) {
      this.map.fitBounds((layer as any).getBounds());
    }
  }

  setExtent(geom: string) {
    if (!geom) {
      return;
    }
    const geoJSONObj = this.parseWKT(geom);
    const layer: Layer = geoJSON(geoJSONObj as any, {style: (feature: any) => ({color: '#627cbb'})});
    this.map.fitBounds((layer as any).getBounds());
  }

  changeBaseLayer(layer: IBaseLayer) {
    if (this.baseLayers.length) {
      this.baseLayers.forEach(item => {
        if (!item) {
          return;
        }
        this.map.removeLayer(item);
      });
    }

    const new_base: TileLayer[] =
      !!layer && layer.sublayers.length
        ? layer.sublayers.map(item => this.createLayer(item) as TileLayer)
        : [this.createLayer(layer) as TileLayer];
    this.baseLayers = new_base;

    if (new_base.length) {
      new_base.forEach(item => {
        if (!item) {
          return;
        }
        item.addTo(this.map);
      });
    }
  }

  addControls() {
    if (this.drawTool) {
      this.addDrawControl();
      this.addClearControl();
      this.setDrawEvents();
    }
    if (this.defaultControls) {
      new Control.Scale({imperial: false}).addTo(this.map);
      new Control.Zoom({
        position: 'bottomright',
        zoomInTitle: 'Приблизить',
        zoomOutTitle: 'Отдалить',
        zoomInText: '',
        zoomOutText: ''
      }).addTo(this.map);
    }
  }

  private createMap() {
    const osmUrl = 'http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const osmAttrib = `&copy; <a href='http://openstreetmap.org/copyright'>OpenStreetMap</a> contributors`;
    const osm = new TileLayer(osmUrl, {maxZoom: 22, maxNativeZoom: 19, attribution: osmAttrib});
    const map = new Map('map', {
      layers: [osm],
      center: new LatLng(66.416667, 94.25),
      zoom: 3,
      worldCopyJump: true,
      zoomControl: false,
      inertia: true,
      fadeAnimation: true,
      zoomAnimation: true,
      boxZoom: false
    });

    this.baseLayers = [osm];
    osm.once('load', () => {
      this.map.invalidateSize(true);
      if (this.layer) {
        this.setExtent(this.layer.map.extent);
      }
    });

    map.setView(this.defaultCenter, this.defaultZoom);

    this.map = map;
    this.addControls();

    if (this.layer) {
      this.addLayer(this.layer);
    }

    map.on('zoomend', (evt) => {
      this.changeDetector.detectChanges();
    });
  }

  private parseWKT(geom: string): DirectGeometryObject {
    const geoJSONObj = Utils.parseWKT(geom);
    if (geoJSONObj.crs && geoJSONObj.crs.properties.name.endsWith('3857')) {
      this.convert3857to4326(geoJSONObj);
    }
    return geoJSONObj;
  }

  private setup() {
    if (this.geometry) {
      this.setExtent(this.geometry);
      this.showGeometry(this.geometry);
    }
  }

  private createLayer(layer: IMapLayer): Layer {
    let l: Layer;

    if (!!layer === false || !layer.type) {
      return null;
    }

    switch (layer.type) {
      case 'wms':
      case 'wmst':
        l = new TileLayer.WMS(layer.url, {
          layers: this.getLayersParam(layer),
          tileSize: layer.tileSize ? layer.tileSize : 256,
          format: 'image/png',
          transparent: true,
          updateWhenZooming: false,
          maxZoom: 22,
          maxNativeZoom: layer.maxNativeZoom ? layer.maxNativeZoom : 18
        });
        break;
      case 'tms':
        const url: string = layer.url;
        l = new TileLayer(url, {
          updateWhenZooming: false,
          maxZoom: 22,
          maxNativeZoom: layer.maxNativeZoom ? layer.maxNativeZoom : 18
        });
        break;
      case 'geojson':
        for (const lr of layer.map.layers) {
          this.leafletLayers[lr.id] = L.markerClusterGroup({
            iconCreateFunction: (cluster) => this.clusterFunction.call(this, lr, cluster)
          });
        }
        break;
      default:
        console.error(`Unknown layer type: ${layer.type}.`);
        break;
    }

    return l;
  }

  addLayer(layer: IMapLayer) {
    for (const id in this.leafletLayers) {
      this.leafletLayers[id].remove();
    }
    this.leafletLayers = {};
    this.layer = layer;
    const leafletLayer = this.createLayer(layer);
    this.refreshLayerData();
    for (const id in this.leafletLayers) {
      this.leafletLayers[id].addTo(this.map);
    }
    if (leafletLayer) {
      this.leafletLayers[layer.id] = leafletLayer;
      leafletLayer.addTo(this.map);
    }
  }

  refreshLayerData() {
    if (this.layer.type === 'geojson') {
      for (const lr of this.layer.map.layers) {
        const clusterLayer = this.leafletLayers[lr.id] as L.MarkerClusterGroup;
        clusterLayer.clearLayers();
        let latlng: LatLng;
        // symbolizer.figure = 'st';
        const featureCollection = JSON.parse(lr.geojson) as FeatureCollection<DirectGeometryObject, any>;
        for (const feature of featureCollection.features) {
          latlng = null;
          if (feature.geometry.type === 'Point') {
            latlng = new LatLng((feature.geometry as GeoJsonPoint).coordinates[1], (feature.geometry as GeoJsonPoint).coordinates[0]);
          } else if (feature.geometry.type === 'MultiPoint') {
            latlng = new LatLng(feature.geometry.coordinates[0][1], feature.geometry.coordinates[0][0]);
          }
          if (latlng) {
            const symbolizer = lr.symbolizers[0];
            const markerOptions = this.getMarkerOptions(symbolizer, feature.properties);
            const marker = L.marker(latlng, markerOptions);
            clusterLayer.addLayer(marker);
          }
        }
      }
    }
  }

  private getMarkerOptions(symbolizer: any, properties: any): L.MarkerOptions {
    const newSymbolizer = Object.assign({}, symbolizer);

    if (newSymbolizer.attributes) {
      for (const attrSymbolizer of newSymbolizer.attributes) {
        if (attrSymbolizer.values) {
          for (const rangeValue of attrSymbolizer.values) {
            if (properties[attrSymbolizer.name] === rangeValue.name) {
              newSymbolizer[attrSymbolizer.styleProperty] = rangeValue.color;
              if (rangeValue.file) {
                newSymbolizer.file = rangeValue.file;
              }
            }
          }
        }
        if (attrSymbolizer.range) {
          for (let iRange = 0; iRange < attrSymbolizer.range.length - 1; iRange++) {
            if (properties[attrSymbolizer.name] >= attrSymbolizer.range[iRange] && properties[attrSymbolizer.name] < attrSymbolizer.range[iRange + 1]) {
              newSymbolizer[attrSymbolizer.styleProperty] = attrSymbolizer.colors[iRange];
            }
          }
        }
      }
    }
    const options: MarkerOptions = {
      fillColor: newSymbolizer.fill,
      strokeColor: newSymbolizer.stroke,
      strokeWidth: newSymbolizer.stroke_width,
      opacity: newSymbolizer.fill_opacity,
      iconSize: L.point(newSymbolizer.size, newSymbolizer.size)
    };

    let icon: DivIcon;
    if (newSymbolizer.file) {
      const ratio = newSymbolizer.file.height / newSymbolizer.file.width;
      icon = new Icon({
        iconUrl: newSymbolizer.file.image,
        iconAnchor: [newSymbolizer.offset_x, newSymbolizer.offset_y],
        iconSize: [newSymbolizer.size, newSymbolizer.size * ratio]
      });
    } else {
      switch (newSymbolizer.figure) {
        case 'el':
          icon = new CircleCanvasIcon(options);
          break;
        case 'sq':
          icon = new SquareCanvasIcon(options);
          break;
        case 'tr':
          icon = new TriangleCanvasIcon(options);
          break;
        case 'st':
          icon = new StarCanvasIcon(options);
          break;
        default:
          newSymbolizer.figure = 'el';
          icon = new CircleCanvasIcon(options);
      }
    }
    return {icon: icon, opacity: newSymbolizer.fill_opacity};
  }

  private clusterFunction(layer: ILayer, cluster: L.MarkerCluster): L.Icon|DivIcon {
    const count = cluster.getChildCount();
    const symbolizer = layer.symbolizers[0];
    const radius = symbolizer.c_size;
    let style = `border-radius:${radius}px;background-color:${symbolizer.c_fill};border:${symbolizer.c_stroke} solid ${symbolizer.c_stroke_width}px;
    color:${symbolizer.t_fill};font-size:${symbolizer.t_size}px;`;
    if (symbolizer.font_name === 'bold') {
      style += 'font-weight:bold;';
    } else if (symbolizer.font_name === 'italic') {
      style += 'font-style:italic;';
    }
    return new DivIcon({
      html: `<div style="${style}"><div>${count}</div></div>`,
      className: 'marker-cluster-circle',
      iconSize: new L.Point(2 * radius, 2 * radius)
    });
  }

  private pointToLayer(feature: Feature<GeometryObject, any>, latlng: LatLng): Layer {
    const symbolizer = this.layer.map.layers[0].symbolizers[0];
    return L.circleMarker(latlng, {
      radius: symbolizer.size / 2,
      fillColor: symbolizer.fill,
      color: symbolizer.stroke,
      weight: symbolizer.stroke_width,
      opacity: symbolizer.fill_opacity,
      fillOpacity: symbolizer.fill_opacity
    });
  }

  private styleFunction(feature: Feature<GeometryObject, any>): any {
    const symbolizer = this.layer.map.layers[0].symbolizers[0];
  }

  private getLayersParam(layer: IMapLayer): string {
    let ret: string = null;
    if (layer.isBaseMap) {
      ret = layer.layers;
    } else if (layer.map && layer.map.id) {
      ret = layer.map.id.toString();
    } else {
      ret = layer.name;
    }
    return ret;
  }

  private setDrawEvents() {
    this.map.on('draw:created', (e: any) => {
      const rectangle = e.layer;
      const rectangleJson = JSON.stringify((e.layer as any).toGeoJSON().geometry);
      const rectangleWKT = Utils.stringifyWKT((e.layer as any).toGeoJSON());

      // Передаем геометрию в родительский компонент
      if (this.outputFormat === 'wkt') {
        this.drawEnd.emit('SRID=4326;' + rectangleWKT);
      } else {
        this.drawEnd.emit(rectangleJson);
      }

      // Отображаем объект на карте
      this.drawnItems.addLayer(rectangle);
    });

    // Очистить карту, если начали рисовать новый объект
    this.map.on('draw:drawstart', e => {
      this.clearMap();
      this.onClear.emit('');
    });
  }

  private addDrawControl() {
    this.drawnItems = new FeatureGroup([]);
    this.map.addLayer(this.drawnItems);

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polyline: false,
        circle: false,
        polygon: false,
        marker: false,
        circlemarker: false
      },
      edit: false
    } as any);

    (L as any).drawLocal.draw.toolbar.buttons.rectangle = 'Нарисовать прямоугольник';
    (L as any).drawLocal.draw.handlers.rectangle.tooltip.start =
      'Зажмите левую кнопку мыши, чтобы нарисовать прямоугольник';
    (L as any).drawLocal.draw.handlers.simpleshape.tooltip.end = 'Отпустите кнопку для завершения';

    this.map.addControl(drawControl);

    (drawControl as any).setDrawingOptions({
      rectangle: {
        shapeOptions: {
          color: '#627cbb'
        }
      }
    });
  }

  private addClearControl() {
    (Control as any).RemoveAll = Control.extend({
      options: {
        position: 'topleft'
      },

      onAdd: (map: Map) => {
        const controlDiv = DomUtil.create('div', 'leaflet-control leaflet-draw-enhanced');
        const controlUI = DomUtil.create('a', 'leaflet-draw-remove', controlDiv);
        controlUI.title = 'Очистить карту';
        controlUI.setAttribute('href', '#');

        DomEvent.addListener(controlUI, 'click', DomEvent.stopPropagation)
          .addListener(controlUI, 'click', DomEvent.preventDefault)
          .addListener(controlUI, 'click', () => {
            this.clearMap();
            this.onClear.emit('');
          });
        return controlDiv;
      }
    });
    const removeAllControl = new (Control as any).RemoveAll();
    this.map.addControl(removeAllControl);
  }

  private convert3857to4326(geoJSONObj: DirectGeometryObject) {
    const newCoords: number[][] = [];

    (geoJSONObj as Polygon).coordinates[0].forEach((coord: number[]) => {
      const point = new Point(coord[0], coord[1]);
      const newcoords = Projection.SphericalMercator.unproject(point);
      newCoords.push([newcoords.lng, newcoords.lat]);
    });

    geoJSONObj.coordinates[0] = newCoords;
  }
}
